import type {Options, SeriesOptionsType, XAxisOptions, YAxisOptions} from 'highcharts';
import CubeIndex from '../cube/CubeIndex';
import {itemColor} from '../render/colors';
import {axisLabelFormat, countHtml, escapeHtml, formatValue, respondents} from '../render/format';
import {isLowBase, lowBaseAttributes} from '../render/lowBase';
import {Cube, CubeCellValue, CubeMeasure, MarkRenderer, RenderContext} from '../types';
import {drawQuadrantLabels} from './scatterQuadrants';

const LINE_COLOR = '#909090';

const channel = (value: string | string[] | undefined): string | undefined => typeof value === 'string' ? value : undefined;

function axisTitle(measure: CubeMeasure): string {
    const unit = measure.unit?.trim();
    return escapeHtml(measure.label) + (unit ? ` (${escapeHtml(unit)})` : '');
}

function axis(measure: CubeMeasure, lines: number | null): XAxisOptions & YAxisOptions {
    return {
        title: {text: axisTitle(measure)},
        labels: {format: axisLabelFormat(measure)},
        plotLines: lines === null ? [] : [{value: lines, color: LINE_COLOR, width: 1, dashStyle: 'Dash', zIndex: 3}],
    };
}

function resolveLine(setting: number | 'mean' | undefined, values: number[]): number | null {
    if (typeof setting === 'number') {
        return setting;
    }
    // unweighted on purpose: every point (shop, product...) counts the same regardless of its respondent count
    return setting === 'mean' && values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : null;
}

const hasValue = (cell: CubeCellValue | undefined): cell is CubeCellValue & {value: number} =>
    cell?.value !== null && cell?.value !== undefined;

const renderScatter: MarkRenderer = (cube: Cube, spec, ctx: RenderContext) => {
    const index = new CubeIndex(cube);
    const encoding = spec.encoding;
    const pointChannel = channel(encoding.point) ?? cube.dimensions[0]?.id;
    const seriesChannel = channel(encoding.series);
    const xMeasure = index.measure(channel(encoding.x) ?? cube.measures[0]?.id);
    const yMeasure = index.measure(channel(encoding.y) ?? cube.measures[1]?.id);
    const sizeId = channel(encoding.size);
    const sizeMeasure = sizeId ? index.measure(sizeId) : undefined;
    const bubble = sizeMeasure !== undefined;
    const showLabels = spec.options.labels !== false;

    // Total and missing members are aggregates, not entities to position; they would distort the quadrant means
    const pointItems = index.items(pointChannel).filter(item => !item.member?.role);
    const seriesItems = (seriesChannel ? index.items(seriesChannel) : [undefined])
        .filter(item => item?.member?.role !== 'total');
    const allX: number[] = [];
    const allY: number[] = [];

    const series = seriesItems.map((seriesItem, seriesIndex) => {
        const color = itemColor(seriesItem?.member, undefined, seriesIndex, ctx);
        const data = pointItems.flatMap(pointItem => {
            const positions = {
                ...(pointChannel ? {[pointChannel]: pointItem.index} : {}),
                ...(seriesChannel && seriesItem ? {[seriesChannel]: seriesItem.index} : {}),
            };
            const x = index.value(positions, xMeasure.id);
            const y = index.value(positions, yMeasure.id);
            const size = sizeMeasure ? index.value(positions, sizeMeasure.id) : undefined;
            if (!hasValue(x) || !hasValue(y) || (sizeMeasure && !hasValue(size))) {
                return [];
            }
            allX.push(x.value);
            allY.push(y.value);

            const lowBase = isLowBase(cube, x) || isLowBase(cube, y);
            const count = Math.min(respondents(x), respondents(y));
            const heading = [pointItem.label, seriesItem && seriesItem.label !== pointItem.label ? seriesItem.label : '']
                .filter(Boolean).map(escapeHtml).join('<br>');
            const measured: [CubeMeasure, CubeCellValue][] = [[xMeasure, x], [yMeasure, y]];
            if (sizeMeasure && size) {
                measured.push([sizeMeasure, size]);
            }
            const rows = measured.map(([measure, cell]) => `${escapeHtml(measure.label)}: <b>${formatValue(cell.value, measure, ctx)}</b>`);
            const countText = countHtml({...x, count, n: count}, lowBase);

            return [{
                id: pointItem.id,
                name: escapeHtml(pointItem.label),
                x: x.value,
                y: y.value,
                ...(size ? {z: size.value} : {}),
                ...(lowBase ? lowBaseAttributes(bubble ? 'pie' : 'line', color, ctx) : {}),
                custom: {
                    count,
                    lowBase,
                    tooltip: `<span style="color:${color}">●</span> ${heading}<br>${rows.join('<br>')}<br>${countText}`,
                },
            }];
        });

        return {
            type: bubble ? 'bubble' : 'scatter',
            id: seriesItem?.id ?? 'points',
            name: escapeHtml(seriesItem?.label ?? yMeasure.label),
            color,
            data,
        } as SeriesOptionsType;
    });

    const quadrants = spec.options.quadrants as {x: number | 'mean'; y: number | 'mean'} | null | undefined;
    const xLine = quadrants ? resolveLine(quadrants.x, allX) : null;
    const yLine = quadrants ? resolveLine(quadrants.y, allY) : null;
    const xName = escapeHtml(xMeasure.label);
    const yName = escapeHtml(yMeasure.label);

    const options: Options = {
        chart: {
            type: bubble ? 'bubble' : 'scatter',
            events: quadrants ? {
                render() {
                    drawQuadrantLabels(this, {
                        topLeft: `↑ ${yName}<br>↓ ${xName}`,
                        topRight: `↑ ${yName}<br>↑ ${xName}`,
                        bottomLeft: `↓ ${yName}<br>↓ ${xName}`,
                        bottomRight: `↓ ${yName}<br>↑ ${xName}`,
                    });
                },
            } : undefined,
        },
        title: {text: undefined},
        credits: {enabled: false},
        xAxis: axis(xMeasure, xLine),
        yAxis: axis(yMeasure, yLine),
        legend: {enabled: seriesItems.length > 1},
        tooltip: {headerFormat: '', pointFormat: '{point.custom.tooltip}'},
        plotOptions: {
            series: {
                dataLabels: {enabled: showLabels && spec.options.dataLabels !== false, format: '{point.name}'},
            },
        },
        series,
    };

    return {type: 'highcharts', options};
};

export default renderScatter;
